import { useState } from "react"
import { IconButton, Stack, Typography, DialogContent, Dialog } from "@mui/material"
import { Close } from "@mui/icons-material"
import { Product } from "@domain/product/models/Product"
import { getMainSupplierIdForProduct } from "@domain/product/getMainSupplierIdForProduct"

import { usePurchasing } from "@features/purchasing/hooks"
import { PurchaseOrderItemStatus } from "@domain/purchasing"
import { addDays } from "date-fns"
import { PurchaseOrderDraft } from "../details/PurchaseOrderDraft"
import { useBottomSheet } from "@shared/shell/bottomsheet/useBottomSheet"
import { PurchaseOrderProducts } from "../items/PurchaseOrderProducts"
import DialogHeader from "@shared/ui/dialog/DialogHeader"
import ErrorState from "@shared/ui/common/ErrorState"

interface CreatePurchaseOrderFlowProps {
    handleClose: () => Promise<void>;
    products?: Product[];
    selectedProducts?: Product[];
    needsRestock?: boolean;
}

export const CreatePurchaseOrderFlow: React.FC<CreatePurchaseOrderFlowProps> = ({
    handleClose,
    products: initialProducts,
    selectedProducts: initialSelectedProducts,
    needsRestock = false,
}) => {

    const { openBottomSheet, closeBottomSheet } = useBottomSheet();
    const [error, setError] = useState<unknown>(null);

    const { createPurchaseOrder } = usePurchasing();

    const createDraftPurchaseOrder = async (products: Product[], supplierId?: string) => {
        try {
            const orderDate = new Date();

            const purchaseOrder = await createPurchaseOrder({
                supplierId: supplierId ?? getMainSupplierIdForProduct(products[0]),
                items: products.map(product => ({
                    product,
                    productionStartDate: new Date(),
                    productionReadyDate: addDays(new Date(), product.productionDuration ?? 0),
                    status: PurchaseOrderItemStatus.PLANNED,
                    shipment: undefined,
                    eta: addDays(orderDate,
                        (product.productionDuration ?? 0) +
                        (product.defaultShipmentMethod?.duration ?? 0) +
                        (product.processingDuration ?? 0)),

                })),
                orderDate: orderDate,
                draft: true,
            });

            await handleClose();

            openBottomSheet({
                title: 'Purchase Order Created',
                component: <PurchaseOrderDraft purchaseOrderId={purchaseOrder?.id!} />,
                closeOnBackdropClick: false,
            });
        } catch (err) {
            setError(err);
        }
    }


    return (
        <>
            <PurchaseOrderProducts
                handleClose={handleClose}
                selectedProducts={initialSelectedProducts}
                addedProducts={initialProducts}
                handleNext={createDraftPurchaseOrder}
                initialMaxOrderWithinWeeks={needsRestock ? 0 : undefined}
            />

            <Dialog open={error !== null} onClose={closeBottomSheet} fullWidth maxWidth="sm">
                <DialogHeader>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography variant="h6">Error creating purchase order</Typography>
                        <IconButton onClick={() => setError(null)}><Close /></IconButton>
                    </Stack>
                </DialogHeader>

                <DialogContent sx={{ p: 0 }}>
                    <ErrorState error={error} />
                </DialogContent>
            </Dialog>
        </>
    );
}